/**
 * Enterprise Entity Schema Diagram Visualization
 * DBML-style diagram with force-directed layout and field-level edge connections
 * Inspired by dbdiagram.io and other professional ERD tools
 */

import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, Filter, X } from 'lucide-react';

interface EntitySchemaDiagramProps {
  entities: Array<{ id: string; entity_type: string; data: any; source_artifact_id?: string }>;
  entityLabels: Record<string, string>;
  entityCategories: Record<string, string>;
  entityCounts?: Record<string, number>; // Total counts per entity type
  onEntityTypeClick?: (entityType: string) => void;
}

interface EntityTypeNode {
  entityType: string;
  label: string;
  category: string;
  count: number;
  fields: Set<string>;
  x: number;
  y: number;
  width: number;
  height: number;
  vx: number;
  vy: number;
}

interface EntityTypeConnection {
  sourceType: string;
  targetType: string;
  connectingFields: string[];
  confidence: number;
}

interface FieldPosition {
  field: string;
  y: number; // Y position within the entity block
  index: number;
}

const TYPE_COLORS: Record<string, string> = {
  default: '#3B82F6',
  measurement: '#10B981',
  result: '#F59E0B',
  well: '#8B5CF6',
  sample: '#EC4899',
  location: '#06B6D4',
  artifact: '#6B7280',
  water: '#0EA5E9',
  sediment: '#84CC16',
  soil: '#A855F7',
  remediation: '#EF4444',
  quality: '#F97316',
};

function getTypeColor(type: string, category: string): string {
  const typeLower = type.toLowerCase();
  const categoryLower = category.toLowerCase();
  
  if (typeLower.includes('measurement')) return TYPE_COLORS.measurement;
  if (typeLower.includes('result')) {
    if (typeLower.includes('water') || typeLower.includes('tap')) return TYPE_COLORS.water;
    if (typeLower.includes('sediment')) return TYPE_COLORS.sediment;
    if (typeLower.includes('soil')) return TYPE_COLORS.soil;
    return TYPE_COLORS.result;
  }
  if (typeLower.includes('well')) return TYPE_COLORS.well;
  if (typeLower.includes('sample')) return TYPE_COLORS.sample;
  if (typeLower.includes('location')) return TYPE_COLORS.location;
  if (typeLower.includes('artifact')) return TYPE_COLORS.artifact;
  if (typeLower.includes('remediation')) return TYPE_COLORS.remediation;
  if (typeLower.includes('qa') || typeLower.includes('qc') || typeLower.includes('quality')) return TYPE_COLORS.quality;
  
  return TYPE_COLORS.default;
}

// Analyze connections between entity types
// NEW APPROACH: First identify common fields across ALL entity types, then create connections
function analyzeConnections(
  entitiesByType: Record<string, Array<{ id: string; data: any }>>
): EntityTypeConnection[] {
  const connections: EntityTypeConnection[] = [];
  const entityTypes = Object.keys(entitiesByType);
  
  const linkingPatterns = [
    { pattern: /^location_id$/, priority: 10 },
    { pattern: /^sample_id$/, priority: 10 },
    { pattern: /^well_id$/, priority: 10 },
    { pattern: /^site_id$/, priority: 9 },
    { pattern: /^coa_number$/, priority: 9 },
    { pattern: /^lab_sample_id$/, priority: 9 },
    { pattern: /^source_artifact_id$/, priority: 8 },
    { pattern: /^(.+)_id$/, priority: 7 },
    { pattern: /^(.+)_uuid$/, priority: 6 },
    { pattern: /^source_(.+)$/, priority: 5 },
    { pattern: /^(.+)_ref$/, priority: 5 },
  ];
  
  // STEP 1: Identify common fields across ALL entity types
  const fieldPresence = new Map<string, Set<string>>(); // field -> set of entity types that have it
  const fieldValues = new Map<string, Map<string, Set<string>>>(); // entityType -> field -> set of values
  
  entityTypes.forEach(type => {
    const entities = entitiesByType[type] || [];
    const sample = entities.slice(0, Math.min(200, entities.length));
    const typeFields = new Map<string, Set<string>>();
    
    sample.forEach(e => {
      Object.keys(e.data || {}).forEach(field => {
        if (!typeFields.has(field)) typeFields.set(field, new Set());
        const value = String(e.data?.[field] || '').trim();
        if (value) typeFields.get(field)!.add(value);
      });
    });
    
    // Even if no entities, we still want to track that this type exists for field presence
    typeFields.forEach((values, field) => {
      if (!fieldPresence.has(field)) fieldPresence.set(field, new Set());
      fieldPresence.get(field)!.add(type);
      
      if (!fieldValues.has(type)) fieldValues.set(type, new Map());
      fieldValues.get(type)!.set(field, values);
    });
  });
  
  // STEP 2: Find fields that appear in multiple entity types (common linking fields)
  const commonFields = new Map<string, string[]>(); // field -> array of entity types that have it
  
  fieldPresence.forEach((types, field) => {
    if (types.size >= 2) { // Field appears in at least 2 entity types
      // Check if it's a linking field
      let priority = 0;
      for (const { pattern, priority: p } of linkingPatterns) {
        if (pattern.test(field)) {
          priority = p;
          break;
        }
      }
      
      if (priority === 0 && (field.includes('_id') || field.includes('_uuid'))) {
        priority = 3;
      }
      
      if (priority > 0) {
        commonFields.set(field, Array.from(types));
      }
    }
  });
  
  // STEP 3: Create connections for all pairs that share common fields
  for (let i = 0; i < entityTypes.length; i++) {
    for (let j = i + 1; j < entityTypes.length; j++) {
      const typeA = entityTypes[i];
      const typeB = entityTypes[j];
      
      // Find common fields between these two types
      const sharedFields: Array<{ field: string; priority: number; overlap: number }> = [];
      
      commonFields.forEach((types, field) => {
        // Check if both types have this field
        if (!types.includes(typeA) || !types.includes(typeB)) return;
        
        const valuesA = fieldValues.get(typeA)?.get(field);
        const valuesB = fieldValues.get(typeB)?.get(field);
        
        if (!valuesA || !valuesB) return;
        
        // Calculate overlap
        let overlap = 0;
        valuesA.forEach(v => {
          if (valuesB.has(v) && v) overlap++;
        });
        
        const overlapRatio = overlap / Math.max(valuesA.size, valuesB.size, 1);
        
        // Get priority
        let priority = 0;
        for (const { pattern, priority: p } of linkingPatterns) {
          if (pattern.test(field)) {
            priority = p;
            break;
          }
        }
        
        if (priority === 0 && (field.includes('_id') || field.includes('_uuid'))) {
          priority = 3;
        }
        
        // For high-priority linking fields (location_id, sample_id, well_id), be more lenient
        // If the field exists in both types, connect them even with minimal overlap
        const isHighPriorityField = priority >= 9; // location_id, sample_id, well_id
        const minOverlapRatio = isHighPriorityField ? 0.01 : 0.05; // Lower threshold for high-priority fields
        
        if (priority > 0 && (overlap > 0 || isHighPriorityField) && (overlapRatio >= minOverlapRatio || isHighPriorityField)) {
          // For high-priority fields, if they exist in both types, assume connection
          // even if overlap is low (they might just have different value sets)
          const effectiveOverlap = isHighPriorityField && overlap === 0 ? 1 : overlap;
          sharedFields.push({ field, priority, overlap: effectiveOverlap });
        }
      });
      
      // Sort by priority and overlap, take top 3
      sharedFields.sort((a, b) => {
        if (a.priority !== b.priority) return b.priority - a.priority;
        return b.overlap - a.overlap;
      });
      
      const topFields = sharedFields.slice(0, 3).map(c => c.field);
      
      if (topFields.length > 0) {
        // Calculate overall confidence
        let totalOverlap = 0;
        let totalValues = 0;
        
        for (const field of topFields) {
          const valuesA = fieldValues.get(typeA)?.get(field)!;
          const valuesB = fieldValues.get(typeB)?.get(field)!;
          let overlap = 0;
          valuesA.forEach(v => {
            if (valuesB.has(v) && v) overlap++;
          });
          totalOverlap += overlap;
          totalValues += Math.max(valuesA.size, valuesB.size);
        }
        
        const confidence = totalValues > 0 ? Math.min(1.0, totalOverlap / totalValues) : 0.5;
        
        // Lower confidence threshold for high-priority fields (location_id, sample_id, etc.)
        // If we have high-priority fields, connect even with lower confidence
        const hasHighPriorityField = topFields.some(field => {
          for (const { pattern, priority } of linkingPatterns) {
            if (pattern.test(field) && priority >= 9) return true;
          }
          return false;
        });
        
        const minConfidence = hasHighPriorityField ? 0.05 : 0.1;
        
        if (confidence >= minConfidence || (hasHighPriorityField && topFields.length > 0)) {
          connections.push({
            sourceType: typeA,
            targetType: typeB,
            connectingFields: topFields,
            confidence: Math.max(confidence, hasHighPriorityField ? 0.3 : confidence), // Boost confidence for high-priority fields
          });
        }
      }
    }
  }
  
  return connections;
}

// Force-directed layout algorithm (inspired by D3.js force simulation)
class ForceDirectedLayout {
  private nodes: EntityTypeNode[];
  private connections: EntityTypeConnection[];
  private width: number;
  private height: number;
  
  // Force parameters - very stringent to prevent ANY overlaps
  private repulsionStrength = 50000; // Much stronger repulsion
  private attractionStrength = 0.008; // Further reduced attraction
  private centerStrength = 0.0003; // Very weak center force
  private damping = 0.85; // Lower damping for faster convergence
  private minDistance = 550; // Much larger minimum distance between node centers
  private padding = 80; // Larger minimum padding between node edges
  private overlapPenalty = 5.0; // Strong penalty multiplier for overlaps
  private fixedNodes = new Set<string>(); // Nodes being dragged
  
  constructor(
    nodes: EntityTypeNode[],
    connections: EntityTypeConnection[],
    width: number,
    height: number
  ) {
    this.nodes = nodes.map(n => ({ ...n, vx: 0, vy: 0 }));
    this.connections = connections;
    this.width = width;
    this.height = height;
  }
  
  tick(iterations: number = 1) {
    for (let iter = 0; iter < iterations; iter++) {
      // Reset forces
      this.nodes.forEach(node => {
        node.vx = 0;
        node.vy = 0;
      });
      
      // Repulsion between all nodes with collision detection using bounding boxes
      for (let i = 0; i < this.nodes.length; i++) {
        for (let j = i + 1; j < this.nodes.length; j++) {
          const nodeA = this.nodes[i];
          const nodeB = this.nodes[j];
          
          // Skip if both are fixed
          if (this.fixedNodes.has(nodeA.entityType) && this.fixedNodes.has(nodeB.entityType)) {
            continue;
          }
          
          // Calculate bounding boxes
          const aLeft = nodeA.x;
          const aRight = nodeA.x + nodeA.width;
          const aTop = nodeA.y;
          const aBottom = nodeA.y + nodeA.height;
          
          const bLeft = nodeB.x;
          const bRight = nodeB.x + nodeB.width;
          const bTop = nodeB.y;
          const bBottom = nodeB.y + nodeB.height;
          
          // Check for actual overlap
          const overlapX = Math.max(0, Math.min(aRight, bRight) - Math.max(aLeft, bLeft));
          const overlapY = Math.max(0, Math.min(aBottom, bBottom) - Math.max(aTop, bTop));
          const isOverlapping = overlapX > 0 && overlapY > 0;
          
          // Calculate center-to-center distance
          const centerAX = nodeA.x + nodeA.width / 2;
          const centerAY = nodeA.y + nodeA.height / 2;
          const centerBX = nodeB.x + nodeB.width / 2;
          const centerBY = nodeB.y + nodeB.height / 2;
          
          const dx = centerBX - centerAX;
          const dy = centerBY - centerAY;
          const distance = Math.sqrt(dx * dx + dy * dy) || 1;
          
          // Calculate minimum required distance (sum of half-diagonals + padding)
          // Use max width/height instead of diagonal for more conservative spacing
          const aMaxDim = Math.max(nodeA.width, nodeA.height);
          const bMaxDim = Math.max(nodeB.width, nodeB.height);
          const minRequiredDistance = (aMaxDim + bMaxDim) / 2 + this.padding;
          
          // Calculate overlap area for penalty
          const overlapArea = overlapX * overlapY;
          
          // VERY strong repulsion if overlapping or too close
          if (isOverlapping || distance < minRequiredDistance) {
            // Much stronger force when overlapping - proportional to overlap area
            const overlapForce = isOverlapping 
              ? this.overlapPenalty * (1 + overlapArea / (nodeA.width * nodeA.height + nodeB.width * nodeB.height))
              : 2.0;
            
            // Use inverse square law with very strong base force
            const force = (this.repulsionStrength * overlapForce) / (distance * distance + 20);
            const fx = (dx / distance) * force;
            const fy = (dy / distance) * force;
            
            if (!this.fixedNodes.has(nodeA.entityType)) {
              nodeA.vx -= fx;
              nodeA.vy -= fy;
            }
            if (!this.fixedNodes.has(nodeB.entityType)) {
              nodeB.vx += fx;
              nodeB.vy += fy;
            }
          } else if (distance < this.minDistance) {
            // Strong repulsion when close but not overlapping
            const closenessRatio = 1 - (distance / this.minDistance);
            const force = (this.repulsionStrength * (1 + closenessRatio * 2)) / (distance * distance + 100);
            const fx = (dx / distance) * force;
            const fy = (dy / distance) * force;
            
            if (!this.fixedNodes.has(nodeA.entityType)) {
              nodeA.vx -= fx;
              nodeA.vy -= fy;
            }
            if (!this.fixedNodes.has(nodeB.entityType)) {
              nodeB.vx += fx;
              nodeB.vy += fy;
            }
          }
        }
      }
      
      // Attraction along edges (Hooke's law)
      for (const conn of this.connections) {
        const source = this.nodes.find(n => n.entityType === conn.sourceType);
        const target = this.nodes.find(n => n.entityType === conn.targetType);
        if (!source || !target) continue;
        
        const sourceCenterX = source.x + source.width / 2;
        const sourceCenterY = source.y + source.height / 2;
        const targetCenterX = target.x + target.width / 2;
        const targetCenterY = target.y + target.height / 2;
        
        const dx = targetCenterX - sourceCenterX;
        const dy = targetCenterY - sourceCenterY;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        
        // Ideal distance based on connection strength
        const idealDistance = this.minDistance * (1 + conn.confidence);
        const force = (distance - idealDistance) * this.attractionStrength;
        
        const fx = (dx / distance) * force;
        const fy = (dy / distance) * force;
        
        source.vx += fx;
        source.vy += fy;
        target.vx -= fx;
        target.vy -= fy;
      }
      
      // Center force (very weak, only for nodes far from center) - almost disabled
      this.nodes.forEach(node => {
        if (this.fixedNodes.has(node.entityType)) return; // Skip fixed nodes
        
        const centerX = this.width / 2;
        const centerY = this.height / 2;
        const nodeCenterX = node.x + node.width / 2;
        const nodeCenterY = node.y + node.height / 2;
        
        const distFromCenter = Math.sqrt(
          Math.pow(nodeCenterX - centerX, 2) + Math.pow(nodeCenterY - centerY, 2)
        );
        
        // Only apply center force if node is very far from center
        if (distFromCenter > Math.min(this.width, this.height) * 0.6) {
          node.vx += (centerX - nodeCenterX) * this.centerStrength;
          node.vy += (centerY - nodeCenterY) * this.centerStrength;
        }
      });
      
      // Apply velocity with damping and update positions
      this.nodes.forEach(node => {
        // Skip fixed nodes (they're being dragged)
        if (this.fixedNodes.has(node.entityType)) {
          return;
        }
        
        node.vx *= this.damping;
        node.vy *= this.damping;
        node.x += node.vx;
        node.y += node.vy;
        
        // Boundary constraints (keep nodes within bounds with padding)
        // Also add extra repulsion from boundaries
        const boundaryPadding = 80;
        const boundaryRepulsion = 1000;
        
        if (node.x < boundaryPadding) {
          node.vx += boundaryRepulsion * (1 + (boundaryPadding - node.x) / boundaryPadding);
          node.x = Math.max(0, boundaryPadding);
        } else if (node.x + node.width > this.width - boundaryPadding) {
          const excess = (node.x + node.width) - (this.width - boundaryPadding);
          node.vx -= boundaryRepulsion * (1 + excess / boundaryPadding);
          node.x = Math.min(this.width - node.width, this.width - node.width - boundaryPadding);
        }
        
        if (node.y < boundaryPadding) {
          node.vy += boundaryRepulsion * (1 + (boundaryPadding - node.y) / boundaryPadding);
          node.y = Math.max(0, boundaryPadding);
        } else if (node.y + node.height > this.height - boundaryPadding) {
          const excess = (node.y + node.height) - (this.height - boundaryPadding);
          node.vy -= boundaryRepulsion * (1 + excess / boundaryPadding);
          node.y = Math.min(this.height - node.height, this.height - node.height - boundaryPadding);
        }
      });
    }
  }
  
  setNodeFixed(entityType: string, fixed: boolean) {
    if (fixed) {
      this.fixedNodes.add(entityType);
    } else {
      this.fixedNodes.delete(entityType);
    }
  }
  
  setNodePosition(entityType: string, x: number, y: number) {
    const node = this.nodes.find(n => n.entityType === entityType);
    if (node) {
      node.x = x;
      node.y = y;
      node.vx = 0; // Reset velocity when manually positioned
      node.vy = 0;
    }
  }
  
  getNodes(): EntityTypeNode[] {
    return this.nodes;
  }
}

// Calculate initial layout using hierarchical clustering
function calculateInitialLayout(
  entityTypes: string[],
  connections: EntityTypeConnection[],
  width: number,
  height: number,
  blockWidth: number,
  blockHeight: number
): Map<string, { x: number; y: number; width: number; height: number }> {
  const layout = new Map<string, { x: number; y: number; width: number; height: number }>();
  
  // Build connection graph
  const connectionCount = new Map<string, number>();
  const neighbors = new Map<string, Set<string>>();
  
  entityTypes.forEach(type => {
    connectionCount.set(type, 0);
    neighbors.set(type, new Set());
  });
  
  connections.forEach(conn => {
    connectionCount.set(conn.sourceType, (connectionCount.get(conn.sourceType) || 0) + 1);
    connectionCount.set(conn.targetType, (connectionCount.get(conn.targetType) || 0) + 1);
    neighbors.get(conn.sourceType)!.add(conn.targetType);
    neighbors.get(conn.targetType)!.add(conn.sourceType);
  });
  
  // Start with most connected node in center
  const sortedTypes = [...entityTypes].sort((a, b) => {
    return (connectionCount.get(b) || 0) - (connectionCount.get(a) || 0);
  });
  
  if (sortedTypes.length === 0) return layout;
  
  // Place nodes in a wider grid pattern initially for better spacing
  const cols = Math.ceil(Math.sqrt(sortedTypes.length));
  const rows = Math.ceil(sortedTypes.length / cols);
  const minSpacing = 120; // Minimum spacing between nodes
  const spacingX = Math.max(blockWidth + minSpacing, (width - blockWidth * cols) / (cols + 1));
  const spacingY = Math.max(blockHeight + minSpacing, (height - blockHeight * rows) / (rows + 1));
  
  sortedTypes.forEach((type, index) => {
    const col = index % cols;
    const row = Math.floor(index / cols);
    
    // Distribute more evenly across the canvas with larger spacing
    const x = spacingX + col * (blockWidth + spacingX);
    const y = spacingY + row * (blockHeight + spacingY);
    
    layout.set(type, {
      x: Math.max(80, Math.min(width - blockWidth - 80, x)),
      y: Math.max(80, Math.min(height - blockHeight - 80, y)),
      width: blockWidth,
      height: blockHeight,
    });
  });
  
  return layout;
}

export function EntitySchemaDiagram({
  entities,
  entityLabels,
  entityCategories,
  entityCounts = {},
  onEntityTypeClick,
}: EntitySchemaDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [filteredTypes, setFilteredTypes] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());
  const [fieldSearch, setFieldSearch] = useState<Record<string, string>>({});
  const [hoveredConnection, setHoveredConnection] = useState<EntityTypeConnection | null>(null);
  const [layoutSimulation, setLayoutSimulation] = useState<ForceDirectedLayout | null>(null);
  const [draggedNode, setDraggedNode] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Group entities by type - include ALL entity types even if they have no entities
  const entitiesByType = useMemo(() => {
    const grouped: Record<string, Array<{ id: string; data: any }>> = {};
    
    // Initialize all entity types from labels (to ensure all are included)
    Object.keys(entityLabels).forEach(type => {
      grouped[type] = [];
    });
    
    // Add entities
    entities.forEach(e => {
      if (!grouped[e.entity_type]) {
        grouped[e.entity_type] = [];
      }
      grouped[e.entity_type].push(e);
    });
    
    return grouped;
  }, [entities, entityLabels]);

  // Analyze connections
  const connections = useMemo(() => {
    return analyzeConnections(entitiesByType);
  }, [entitiesByType]);

  const filteredConnections = useMemo(() => {
    return connections.filter(
      c => !filteredTypes.has(c.sourceType) && !filteredTypes.has(c.targetType)
    );
  }, [connections, filteredTypes]);

  // Build entity type nodes
  const entityTypeNodes = useMemo(() => {
    const types = Object.keys(entitiesByType).filter(t => !filteredTypes.has(t));
    return types.map(type => {
      const entitiesOfType = entitiesByType[type];
      const allFields = new Set<string>();
      entitiesOfType.forEach(e => {
        Object.keys(e.data || {}).forEach(f => allFields.add(f));
      });
      
      return {
        entityType: type,
        label: entityLabels[type] || type,
        category: entityCategories[type] || 'default',
        count: entityCounts[type] || entitiesOfType.length, // Use total count if available
        fields: allFields,
        x: 0,
        y: 0,
        width: 280,
        height: 200,
        vx: 0,
        vy: 0,
      };
    });
  }, [entitiesByType, entityLabels, entityCategories, filteredTypes]);

  // Calculate layout with force simulation
  const [layout, setLayout] = useState<Map<string, { x: number; y: number; width: number; height: number }>>(new Map());

  useEffect(() => {
    if (!svgRef.current || entityTypeNodes.length === 0) return;
    
    const width = svgRef.current.clientWidth || 1400;
    const height = svgRef.current.clientHeight || 900;
    const blockWidth = 280;
    const blockHeight = 200;
    
    // Calculate initial positions
    const initialLayout = calculateInitialLayout(
      entityTypeNodes.map(n => n.entityType),
      filteredConnections,
      width,
      height,
      blockWidth,
      blockHeight
    );
    
    // Create nodes with initial positions
    const nodes: EntityTypeNode[] = entityTypeNodes.map(node => {
      const pos = initialLayout.get(node.entityType);
      return {
        ...node,
        x: pos?.x || width / 2,
        y: pos?.y || height / 2,
        width: blockWidth,
        height: blockHeight,
        vx: 0,
        vy: 0,
      };
    });
    
    // Create force simulation
    const simulation = new ForceDirectedLayout(nodes, filteredConnections, width, height);
    setLayoutSimulation(simulation);
    
    // Update layout map
    const layoutMap = new Map<string, { x: number; y: number; width: number; height: number }>();
    nodes.forEach(node => {
      layoutMap.set(node.entityType, {
        x: node.x,
        y: node.y,
        width: node.width,
        height: node.height,
      });
    });
    setLayout(layoutMap);
  }, [entityTypeNodes, filteredConnections]);

  // Run force simulation animation with more iterations for better convergence
  useEffect(() => {
    if (!layoutSimulation) return;
    
    let animationFrame: number;
    let lastTime = performance.now();
    let iterationCount = 0;
    const maxIterations = 800; // Run more iterations for better convergence
    
    const animate = (currentTime: number) => {
      const delta = Math.min(currentTime - lastTime, 50); // Cap at 50ms
      lastTime = currentTime;
      
      // Run multiple iterations per frame for faster convergence
      const iterations = Math.max(5, Math.floor(delta / 8)); // More iterations per frame
      layoutSimulation.tick(iterations);
      iterationCount += iterations;
      
      // Update layout
      const nodes = layoutSimulation.getNodes();
      const layoutMap = new Map<string, { x: number; y: number; width: number; height: number }>();
      nodes.forEach(node => {
        layoutMap.set(node.entityType, {
          x: node.x,
          y: node.y,
          width: node.width,
          height: node.height,
        });
      });
      setLayout(layoutMap);
      
      // Continue animating until convergence or max iterations
      if (iterationCount < maxIterations) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    
    return () => {
      cancelAnimationFrame(animationFrame);
    };
  }, [layoutSimulation]);

  const entityTypes = useMemo(() => {
    const types = new Set(entities.map(e => e.entity_type));
    return Array.from(types).sort();
  }, [entities]);

  // Handle node dragging
  const handleNodeMouseDown = useCallback((e: React.MouseEvent, entityType: string) => {
    if (e.button !== 0) return;
    
    const nodeLayout = layout.get(entityType);
    if (!nodeLayout || !layoutSimulation) return;
    
    // Calculate offset from mouse to node position
    const svgRect = svgRef.current?.getBoundingClientRect();
    if (!svgRect) return;
    
    const mouseX = (e.clientX - svgRect.left - pan.x) / zoom;
    const mouseY = (e.clientY - svgRect.top - pan.y) / zoom;
    
    setDraggedNode(entityType);
    setDragOffset({
      x: mouseX - nodeLayout.x,
      y: mouseY - nodeLayout.y,
    });
    
    // Fix the node in the simulation
    layoutSimulation.setNodeFixed(entityType, true);
    
    e.preventDefault();
    e.stopPropagation();
  }, [layout, pan, zoom, layoutSimulation]);

  // Handle node dragging move
  const handleNodeMouseMove = useCallback((e: React.MouseEvent) => {
    if (!draggedNode || !layoutSimulation || !svgRef.current) return;
    
    const svgRect = svgRef.current.getBoundingClientRect();
    const mouseX = (e.clientX - svgRect.left - pan.x) / zoom;
    const mouseY = (e.clientY - svgRect.top - pan.y) / zoom;
    
    const newX = mouseX - dragOffset.x;
    const newY = mouseY - dragOffset.y;
    
    // Update node position
    layoutSimulation.setNodePosition(draggedNode, newX, newY);
    
    // Update layout immediately
    const nodes = layoutSimulation.getNodes();
    const layoutMap = new Map<string, { x: number; y: number; width: number; height: number }>();
    nodes.forEach(node => {
      layoutMap.set(node.entityType, {
        x: node.x,
        y: node.y,
        width: node.width,
        height: node.height,
      });
    });
    setLayout(layoutMap);
    
    e.preventDefault();
  }, [draggedNode, dragOffset, pan, zoom, layoutSimulation]);

  // Handle node drag end
  const handleNodeMouseUp = useCallback(() => {
    if (draggedNode && layoutSimulation) {
      // Unfix the node but keep its position
      layoutSimulation.setNodeFixed(draggedNode, false);
    }
    setDraggedNode(null);
  }, [draggedNode, layoutSimulation]);

  // Improved panning (only when not dragging a node)
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (draggedNode) return; // Don't pan if dragging a node
    
    const target = e.target as HTMLElement;
    const isEntityBlock = target.closest('g[data-entity-block]') || 
                          target.closest('rect[data-entity-block]') ||
                          target.closest('text[data-entity-block]') ||
                          target.closest('foreignObject');
    
    if (e.button === 0 && !isEntityBlock && (target.tagName === 'svg' || target.tagName === 'g' || target.tagName === 'rect')) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
      e.preventDefault();
      e.stopPropagation();
    }
  }, [pan, draggedNode]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // Handle node dragging first
    if (draggedNode) {
      handleNodeMouseMove(e);
      return;
    }
    
    // Then handle panning
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
      e.preventDefault();
    }
  }, [isDragging, dragStart, draggedNode, handleNodeMouseMove]);

  const handleMouseUp = useCallback(() => {
    if (draggedNode) {
      handleNodeMouseUp();
    } else {
      setIsDragging(false);
    }
  }, [draggedNode, handleNodeMouseUp]);

  // Wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.max(0.3, Math.min(3, prev * delta)));
  }, []);

  const toggleTypeFilter = useCallback((type: string) => {
    setFilteredTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const toggleExpand = useCallback((type: string) => {
    setExpandedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  // Calculate field positions for edge connections
  const getFieldPositions = useCallback((
    entityType: string,
    fields: Set<string>,
    connectingFields: Set<string>,
    blockLayout: { x: number; y: number; width: number; height: number },
    isExpanded: boolean,
    searchTerm: string
  ): FieldPosition[] => {
    const headerHeight = 50;
    const searchHeight = (isExpanded && searchTerm) ? 35 : 0;
    const fieldHeight = 20;
    const startY = blockLayout.y + headerHeight + searchHeight + 12;
    
    const allFields = Array.from(fields).sort((a, b) => {
      const aIsConnecting = connectingFields.has(a);
      const bIsConnecting = connectingFields.has(b);
      if (aIsConnecting && !bIsConnecting) return -1;
      if (!aIsConnecting && bIsConnecting) return 1;
      return a.localeCompare(b);
    });
    
    const filteredFields = searchTerm 
      ? allFields.filter(f => f.toLowerCase().includes(searchTerm.toLowerCase()))
      : allFields;
    
    const fieldsToShow = isExpanded ? filteredFields : filteredFields.slice(0, 8);
    
    return fieldsToShow.map((field, idx) => ({
      field,
      y: startY + (idx + 1) * fieldHeight + 14, // Center of field text
      index: idx,
    }));
  }, []);

  if (entities.length === 0) {
    return (
      <div className="flex items-center justify-center h-96 border border-gray-200 dark:border-gray-700 rounded-lg">
        <p className="text-gray-500">No entities to visualize</p>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="relative border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900 overflow-hidden"
      style={{ height: '800px' }}
    >
      {/* Enterprise Controls Bar */}
      <div className="absolute top-0 left-0 right-0 z-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {entityTypeNodes.length} Entity Types • {filteredConnections.length} Relationships
          </div>
          {selectedType && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              Selected: {entityLabels[selectedType] || selectedType}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              showFilters 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            <Filter className="h-4 w-4 inline mr-1" />
            Filter
          </button>
          <button
            onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
            className="px-3 py-1.5 text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            title="Reset view"
          >
            <RotateCcw className="h-4 w-4 inline mr-1" />
            Reset
          </button>
          <div className="flex items-center gap-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800">
            <button
              onClick={() => setZoom(prev => Math.max(0.3, prev - 0.1))}
              className="px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </button>
            <span className="px-2 py-1 text-xs font-mono text-gray-600 dark:text-gray-400 min-w-[3rem] text-center">
              {Math.round(zoom * 100)}%
            </span>
            <button
              onClick={() => setZoom(prev => Math.min(3, prev + 0.1))}
              className="px-2 py-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="absolute top-12 left-4 z-20 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl p-4 w-64 max-h-[600px] overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm text-gray-900 dark:text-white">Filter Entity Types</h3>
            <button
              onClick={() => setShowFilters(false)}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
          <div className="space-y-2">
            {entityTypes.map(type => (
              <label key={type} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 p-1 rounded">
                <input
                  type="checkbox"
                  checked={!filteredTypes.has(type)}
                  onChange={() => toggleTypeFilter(type)}
                  className="rounded border-gray-300"
                />
                <span className="text-gray-700 dark:text-gray-300">{entityLabels[type] || type}</span>
                <span className="ml-auto text-xs text-gray-500">({entitiesByType[type]?.length || 0})</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* SVG Diagram */}
      <svg
        ref={svgRef}
        width="100%"
        height="100%"
        className={`${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        style={{ touchAction: 'none', userSelect: 'none' }}
      >
        <defs>
          {/* Arrow marker */}
          <marker
            id="arrowhead"
            markerWidth="12"
            markerHeight="12"
            refX="10"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 12 3.5, 0 7" fill="#6366F1" />
          </marker>
          
          {/* Drop shadow filter */}
          <filter id="dropshadow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="3"/>
            <feOffset dx="2" dy="2" result="offsetblur"/>
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.3"/>
            </feComponentTransfer>
            <feMerge>
              <feMergeNode/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Background for panning */}
        <rect
          width="100%"
          height="100%"
          fill="transparent"
          onMouseDown={handleMouseDown}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        />
        
        <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {/* Connection Lines - FIELD-LEVEL CONNECTIONS */}
          {filteredConnections.map((conn, idx) => {
            const sourceLayout = layout.get(conn.sourceType);
            const targetLayout = layout.get(conn.targetType);
            
            if (!sourceLayout || !targetLayout) return null;
            
            const sourceNode = entityTypeNodes.find(n => n.entityType === conn.sourceType);
            const targetNode = entityTypeNodes.find(n => n.entityType === conn.targetType);
            if (!sourceNode || !targetNode) return null;
            
            // Get connecting fields
            const sourceConnectingFields = new Set(conn.connectingFields);
            const targetConnectingFields = new Set(conn.connectingFields);
            
            // Get field positions
            const sourceFieldPositions = getFieldPositions(
              conn.sourceType,
              sourceNode.fields,
              sourceConnectingFields,
              sourceLayout,
              expandedTypes.has(conn.sourceType),
              fieldSearch[conn.sourceType] || ''
            );
            
            const targetFieldPositions = getFieldPositions(
              conn.targetType,
              targetNode.fields,
              targetConnectingFields,
              targetLayout,
              expandedTypes.has(conn.targetType),
              fieldSearch[conn.targetType] || ''
            );
            
            // Find the primary connecting field
            const primaryField = conn.connectingFields[0];
            const sourceFieldPos = sourceFieldPositions.find(fp => fp.field === primaryField);
            const targetFieldPos = targetFieldPositions.find(fp => fp.field === primaryField);
            
            // Use field positions if available, otherwise use block centers
            const x1 = sourceLayout.x + sourceLayout.width;
            const y1 = sourceFieldPos ? sourceFieldPos.y : (sourceLayout.y + sourceLayout.height / 2);
            const x2 = targetLayout.x;
            const y2 = targetFieldPos ? targetFieldPos.y : (targetLayout.y + targetLayout.height / 2);
            
            const isSelected = selectedType === conn.sourceType || selectedType === conn.targetType;
            const isHovered = hoveredConnection === conn;
            const strokeWidth = isSelected || isHovered ? 3.5 : 2.5;
            const strokeColor = isSelected ? '#3B82F6' : isHovered ? '#8B5CF6' : '#6366F1';
            const opacity = 0.8 + (conn.confidence * 0.2);
            
            // Smart edge routing to minimize overlaps
            // Calculate path that avoids other nodes
            const dx = x2 - x1;
            const dy = y2 - y1;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            // Find other connections between same nodes to offset this one
            const sameConnections = filteredConnections.filter((c, i) => 
              i !== idx && 
              ((c.sourceType === conn.sourceType && c.targetType === conn.targetType) ||
               (c.sourceType === conn.targetType && c.targetType === conn.sourceType))
            );
            const connectionIndex = sameConnections.length;
            const totalSameConnections = sameConnections.length + 1;
            
            // Offset multiple connections between same nodes
            const perpendicularOffset = (connectionIndex - (totalSameConnections - 1) / 2) * 15;
            const perpX = distance > 0 ? -dy / distance : 0;
            const perpY = distance > 0 ? dx / distance : 0;
            const offsetX = perpX * perpendicularOffset;
            const offsetY = perpY * perpendicularOffset;
            
            // Curved path with offset to avoid overlaps
            const midX = (x1 + x2) / 2 + offsetX;
            const midY = (y1 + y2) / 2 + offsetY;
            const curveOffset = Math.abs(x2 - x1) * 0.4;
            const cp1x = x1 + curveOffset * (distance > 0 ? dx / distance : 0) + offsetX * 0.5;
            const cp1y = y1 + curveOffset * (distance > 0 ? dy / distance : 0) + offsetY * 0.5;
            const cp2x = x2 - curveOffset * (distance > 0 ? dx / distance : 0) + offsetX * 0.5;
            const cp2y = y2 - curveOffset * (distance > 0 ? dy / distance : 0) + offsetY * 0.5;
            
            return (
              <g 
                key={idx}
                onMouseEnter={() => setHoveredConnection(conn)}
                onMouseLeave={() => setHoveredConnection(null)}
              >
                {/* Main connection line with smart routing */}
                <path
                  d={`M ${x1} ${y1} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${x2} ${y2}`}
                  fill="none"
                  stroke={strokeColor}
                  strokeWidth={strokeWidth}
                  strokeOpacity={opacity}
                  markerEnd="url(#arrowhead)"
                  filter="url(#dropshadow)"
                  style={{ cursor: 'pointer' }}
                />
                {/* Connection label - positioned on the offset path */}
                <g transform={`translate(${midX}, ${midY - 15 + offsetY})`}>
                  <rect
                    x={-60}
                    y={-12}
                    width={120}
                    height={24}
                    fill="white"
                    stroke={strokeColor}
                    strokeWidth={2}
                    rx={6}
                    opacity={0.95}
                    filter="url(#dropshadow)"
                  />
                  <text
                    x={0}
                    y={5}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#1F2937"
                    fontWeight="600"
                    className="pointer-events-none select-none"
                  >
                    {primaryField || 'linked'}
                    {conn.connectingFields.length > 1 && ` (+${conn.connectingFields.length - 1})`}
                  </text>
                </g>
              </g>
            );
          })}

          {/* Entity Type Blocks */}
          {entityTypeNodes.map(node => {
            const nodeLayout = layout.get(node.entityType);
            if (!nodeLayout) return null;
            
            const isSelected = selectedType === node.entityType;
            const isExpanded = expandedTypes.has(node.entityType);
            const color = getTypeColor(node.entityType, node.category);
            
            // Get connecting fields
            const connectingFields = new Set<string>();
            filteredConnections.forEach(conn => {
              if (conn.sourceType === node.entityType || conn.targetType === node.entityType) {
                conn.connectingFields.forEach(f => connectingFields.add(f));
              }
            });
            
            // Get ALL fields, sorted by importance
            const allFields = Array.from(node.fields).sort((a, b) => {
              const aIsConnecting = connectingFields.has(a);
              const bIsConnecting = connectingFields.has(b);
              if (aIsConnecting && !bIsConnecting) return -1;
              if (!aIsConnecting && bIsConnecting) return 1;
              
              const importantFields = ['id', 'location_id', 'sample_id', 'well_id', 'site_id', 'coa_number', 'sample_date', 'analysis_date'];
              const aImportant = importantFields.some(f => a.includes(f));
              const bImportant = importantFields.some(f => b.includes(f));
              if (aImportant && !bImportant) return -1;
              if (!aImportant && bImportant) return 1;
              
              return a.localeCompare(b);
            });
            
            // Filter fields by search if present
            const searchTerm = fieldSearch[node.entityType]?.toLowerCase() || '';
            const filteredFields = searchTerm 
              ? allFields.filter(f => f.toLowerCase().includes(searchTerm))
              : allFields;
            
            // Calculate dynamic height
            const fieldsToShow = isExpanded ? filteredFields : filteredFields.slice(0, 8);
            const fieldHeight = 20;
            const headerHeight = 50;
            const padding = 16;
            const searchHeight = (isExpanded && searchTerm) ? 35 : 0;
            const dynamicHeight = headerHeight + searchHeight + (fieldsToShow.length * fieldHeight) + padding;
            const blockHeight = Math.max(200, dynamicHeight);
            
            // Get field positions for highlighting
            const fieldPositions = getFieldPositions(
              node.entityType,
              node.fields,
              connectingFields,
              nodeLayout,
              isExpanded,
              searchTerm
            );
            
            return (
              <g
                key={node.entityType}
                data-entity-block
                onClick={(e) => {
                  // Only allow selection for visual feedback, don't open modal in graph view
                  if (!draggedNode) {
                    e.stopPropagation();
                    setSelectedType(selectedType === node.entityType ? null : node.entityType);
                    // Removed onEntityTypeClick to prevent modal from opening in graph view
                  }
                }}
                onMouseDown={(e) => {
                  handleNodeMouseDown(e, node.entityType);
                }}
                className={draggedNode === node.entityType ? "cursor-grabbing" : "cursor-grab"}
              >
                {/* Main Block with Shadow */}
                <rect
                  x={nodeLayout.x}
                  y={nodeLayout.y}
                  width={nodeLayout.width}
                  height={blockHeight}
                  fill="white"
                  fillOpacity={draggedNode === node.entityType ? 1 : 0.98}
                  stroke={isSelected ? '#3B82F6' : draggedNode === node.entityType ? '#8B5CF6' : color}
                  strokeWidth={isSelected ? 4 : draggedNode === node.entityType ? 3 : 2.5}
                  rx={8}
                  filter={draggedNode === node.entityType ? "url(#dropshadow)" : "url(#dropshadow)"}
                  style={{ 
                    cursor: draggedNode === node.entityType ? 'grabbing' : 'grab',
                    pointerEvents: 'all'
                  }}
                />
                
                {/* Header Bar */}
                <rect
                  x={nodeLayout.x}
                  y={nodeLayout.y}
                  width={nodeLayout.width}
                  height={headerHeight}
                  fill={color}
                  fillOpacity={0.95}
                  rx={8}
                />
                
                {/* Entity Type Name */}
                <text
                  x={nodeLayout.x + 12}
                  y={nodeLayout.y + 30}
                  fontSize="13"
                  fontWeight="700"
                  fill="white"
                  className="pointer-events-none select-none"
                >
                  {node.label}
                </text>
                
                {/* Entity Count Badge */}
                <rect
                  x={nodeLayout.x + nodeLayout.width - 50}
                  y={nodeLayout.y + 8}
                  width={42}
                  height={24}
                  rx={12}
                  fill="white"
                  fillOpacity={0.25}
                />
                <text
                  x={nodeLayout.x + nodeLayout.width - 29}
                  y={nodeLayout.y + 25}
                  fontSize="11"
                  fontWeight="700"
                  fill="white"
                  textAnchor="middle"
                  className="pointer-events-none select-none"
                >
                  {node.count}
                </text>
                
                {/* Field Search (if expanded) */}
                {isExpanded && (
                  <g>
                    <rect
                      x={nodeLayout.x + 8}
                      y={nodeLayout.y + headerHeight + 8}
                      width={nodeLayout.width - 16}
                      height={28}
                      fill="#F9FAFB"
                      stroke="#E5E7EB"
                      strokeWidth={1}
                      rx={4}
                    />
                    {/* Search icon */}
                    <g transform={`translate(${nodeLayout.x + 14}, ${nodeLayout.y + headerHeight + 14})`}>
                      <circle cx="6" cy="6" r="3.5" fill="none" stroke="#9CA3AF" strokeWidth="1.5" />
                      <line x1="9" y1="9" x2="12" y2="12" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" />
                    </g>
                    <foreignObject
                      x={nodeLayout.x + 12}
                      y={nodeLayout.y + headerHeight + 10}
                      width={nodeLayout.width - 24}
                      height={24}
                    >
                      <input
                        type="text"
                        value={fieldSearch[node.entityType] || ''}
                        onChange={(e) => {
                          e.stopPropagation();
                          setFieldSearch(prev => ({
                            ...prev,
                            [node.entityType]: e.target.value,
                          }));
                        }}
                        onClick={(e) => e.stopPropagation()}
                        placeholder="Search fields..."
                        className="w-full h-full px-2 pl-8 text-xs border-0 bg-transparent focus:outline-none text-gray-700 dark:text-gray-300"
                      />
                    </foreignObject>
                  </g>
                )}
                
                {/* Fields List - ALL FIELDS when expanded */}
                <g transform={`translate(${nodeLayout.x + 12}, ${nodeLayout.y + headerHeight + searchHeight + 12})`}>
                  <text
                    x={0}
                    y={0}
                    fontSize="10"
                    fontWeight="600"
                    fill="#6B7280"
                    className="pointer-events-none select-none"
                  >
                    Fields ({filteredFields.length}):
                  </text>
                  
                  {fieldsToShow.length > 0 ? (
                    fieldsToShow.map((field, idx) => {
                      const isConnecting = connectingFields.has(field);
                      const fieldPos = fieldPositions.find(fp => fp.field === field);
                      const isHovered = hoveredConnection && 
                        (hoveredConnection.sourceType === node.entityType || hoveredConnection.targetType === node.entityType) &&
                        hoveredConnection.connectingFields.includes(field);
                      
                      return (
                        <g key={field} transform={`translate(0, ${(idx + 1) * fieldHeight})`}>
                          {/* Highlight connecting fields */}
                          {isConnecting && (
                            <rect
                              x={-4}
                              y={0}
                              width={nodeLayout.width - 8}
                              height={fieldHeight}
                              fill={isHovered ? '#DBEAFE' : '#EFF6FF'}
                              fillOpacity={isHovered ? 0.8 : 0.5}
                              rx={2}
                              className="pointer-events-none"
                            />
                          )}
                          {/* Left border indicator for connecting fields */}
                          {isConnecting && (
                            <rect
                              x={-4}
                              y={0}
                              width={3}
                              height={fieldHeight}
                              fill="#3B82F6"
                              fillOpacity={isHovered ? 1 : 0.7}
                              rx={1}
                              className="pointer-events-none"
                            />
                          )}
                          <text
                            x={0}
                            y={14}
                            fontSize="9"
                            fill={isConnecting ? '#3B82F6' : '#374151'}
                            fontWeight={isConnecting ? '600' : '400'}
                            className="pointer-events-none select-none font-mono"
                          >
                            {isConnecting && '🔗 '}
                            {field}
                          </text>
                        </g>
                      );
                    })
                  ) : (
                    <text
                      x={0}
                      y={fieldHeight}
                      fontSize="9"
                      fill="#9CA3AF"
                      className="pointer-events-none select-none"
                    >
                      No fields match search
                    </text>
                  )}
                  
                  {/* Expand/Collapse Button */}
                  {filteredFields.length > 8 && (
                    <g
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpand(node.entityType);
                      }}
                      className="cursor-pointer"
                    >
                      <rect
                        x={0}
                        y={fieldsToShow.length * fieldHeight + 8}
                        width={nodeLayout.width - 24}
                        height={24}
                        fill="#F3F4F6"
                        stroke="#D1D5DB"
                        strokeWidth={1}
                        rx={4}
                        className="hover:fill-gray-200 dark:hover:fill-gray-700"
                      />
                      <g transform={`translate(${(nodeLayout.width - 24) / 2}, ${fieldsToShow.length * fieldHeight + 12})`}>
                        {isExpanded ? (
                          <>
                            <path
                              d="M -35 2 L -30 7 L -25 2"
                              stroke="#4B5563"
                              strokeWidth="2"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <text
                              x={0}
                              y={10}
                              fontSize="9"
                              fontWeight="600"
                              fill="#4B5563"
                              textAnchor="middle"
                              className="pointer-events-none select-none"
                            >
                              Show Less ({filteredFields.length - 8} hidden)
                            </text>
                          </>
                        ) : (
                          <>
                            <path
                              d="M -45 7 L -40 2 L -35 7"
                              stroke="#4B5563"
                              strokeWidth="2"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <text
                              x={0}
                              y={10}
                              fontSize="9"
                              fontWeight="600"
                              fill="#4B5563"
                              textAnchor="middle"
                              className="pointer-events-none select-none"
                            >
                              Show All {filteredFields.length} Fields
                            </text>
                          </>
                        )}
                      </g>
                    </g>
                  )}
                </g>
              </g>
            );
          })}
        </g>
      </svg>
      
      {/* Help Text */}
      <div className="absolute bottom-4 right-4 z-10 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm p-3 text-xs max-w-xs">
        <div className="font-semibold text-gray-900 dark:text-white mb-2">Controls</div>
        <div className="space-y-1 text-gray-600 dark:text-gray-400">
          <div>• Drag entity blocks to reposition</div>
          <div>• Click & drag background to pan</div>
          <div>• Scroll to zoom</div>
          <div>• Click entity to select</div>
          <div>• Hover connections to highlight fields</div>
        </div>
      </div>
    </div>
  );
}
